import React, { useState } from 'react';
import {
    Controller,
    type ControllerProps,
    type FieldPath,
    type FieldValues,
    FormProvider,
    type Path,
    type SubmitHandler,
    type UseFormReturn,
    get,
    useFormContext,
    useFormState,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { recordError } from '../errorLog.ts';
import { getErrorField, getErrorMessageKey } from '../i18n/errors.ts';
import Disclosure, { type DisclosureProps } from './Disclosure.tsx';
import ErrorMessage from './ErrorMessage.tsx';
import Slot from './Slot.tsx';

// TTransformedValues are the values after validation (e.g. a zod schema's output), which onSubmit receives.
export interface FormProps<
    TFieldValues extends FieldValues = FieldValues,
    TTransformedValues = TFieldValues,
> extends Omit<React.ComponentPropsWithRef<'form'>, 'onSubmit'> {
    form: UseFormReturn<TFieldValues, unknown, TTransformedValues>;
    onSubmit: SubmitHandler<TTransformedValues>;
}

const FormRoot = <TFieldValues extends FieldValues = FieldValues, TTransformedValues = TFieldValues>({
    form,
    onSubmit,
    children,
    ...props
}: FormProps<TFieldValues, TTransformedValues>) => {
    // A failed submit is shown on the field it's about (e.g. a taken slug), otherwise above the buttons (Form.Error).
    const submit = async (values: TTransformedValues) => {
        try {
            await onSubmit(values);
        } catch (error) {
            console.error(error);
            recordError(error);
            const field = getErrorField(error);
            const message = getErrorMessageKey(error);
            if (field && get(form.getValues(), field) !== undefined) {
                form.setError(field as Path<TFieldValues>, { message }, { shouldFocus: true });
            } else {
                form.setError('root', { message });
            }
        }
    };

    return (
        <FormProvider {...form}>
            <form
                noValidate
                {...props}
                onSubmit={(event) => {
                    event.preventDefault();
                    // React events bubble through portals: a form in a dialog would otherwise also submit the form
                    // the dialog was opened from.
                    event.stopPropagation();
                    void form.handleSubmit(submit)(event);
                }}
            >
                {children}
            </form>
        </FormProvider>
    );
};

export interface FormFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> extends Omit<
    ControllerProps<TFieldValues, TName>,
    'render' | 'control'
> {
    children: React.ReactElement;
}

// Schemas use translation keys as messages (e.g. 'validation.required') so they stay language independent.
export const useTranslateMessage = () => {
    const { t, i18n } = useTranslation();
    return (message: string | undefined) =>
        message && i18n.exists(message) ? t(message as 'validation.required') : message;
};

// Binds a field component to the form: value, onChange, onBlur, ref, isInvalid and errorMessage are injected.
const FormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
    children,
    ...props
}: FormFieldProps<TFieldValues, TName>) => {
    const { control } = useFormContext<TFieldValues>();
    const translate = useTranslateMessage();

    // fieldState (not the context's formState) re-renders on validation, also for nested names like 'line_items.0.net_amount'.
    return (
        <Controller
            {...props}
            control={control}
            render={({ field, fieldState }) => (
                <Slot {...field} isInvalid={fieldState.invalid} errorMessage={translate(fieldState.error?.message)}>
                    {children}
                </Slot>
            )}
        />
    );
};

// Where a failed submit that isn't about one field is shown; forms place it above their buttons.
const FormError = () => {
    const { errors } = useFormState();
    return <ErrorMessage message={errors.root?.message} />;
};

export interface FormDisclosureProps<TFieldValues extends FieldValues> extends Omit<
    DisclosureProps,
    'isExpanded' | 'defaultExpanded' | 'onExpandedChange'
> {
    // The fields in it, so it can open itself when one of them has an error, which wouldn't show while it's closed.
    fields: FieldPath<TFieldValues>[];
}

// A Disclosure of form fields, e.g. settings most people never need.
const FormDisclosure = <TFieldValues extends FieldValues>({ fields, ...props }: FormDisclosureProps<TFieldValues>) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { errors } = useFormState<TFieldValues>({ name: fields });
    const hasError = fields.some((field) => get(errors, field) !== undefined);

    return <Disclosure {...props} isExpanded={isExpanded || hasError} onExpandedChange={setIsExpanded} />;
};

export default Object.assign(FormRoot, {
    Field: FormField,
    Error: FormError,
    Disclosure: FormDisclosure,
});
